import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { BaseDataService } from "@bbwt/modules/data-service/base.data.service";
import { HttpResponsesHandlersFactory } from "@bbwt/modules/data-service/http-responses-handler";
import {
    AuthenticationSettings,
    U2fAuthenticationResponse,
    U2fRegistrationRequest,
    U2fRegistrationResponse,
    RecoverPassword,
    ISignUpResult
} from "../interfaces";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { LoginRequest } from "@account/interfaces/login-request";
import { PatientLogin, UserLogin, UserLogins } from "@main/users";
import { RecoveryCode } from "@account/interfaces/recovery-code";
import { AppStorage } from "@bbwt/utils/app-storage";
import { LinkNHSRequest } from "@account/interfaces/link-nhs-request";
import { SetLocalTimeZone } from "@bbwt/utils";
import { DateOfBirthMismatchNormalized } from "@mipro/classes/nhs-mipro-data-mismatch";
import { ActivateAccountResult } from "./account-activate-result";
import { IM1ApiSettings } from "@main/system-configuration/classes/im1-api-settings";
import { MessageTrackingService } from "@main/home/messageTracking.service";
import { NhsRegistrationResponse, SaveNHSTokenRegistrationPatientAction } from "@main/profile/nhs/nhs-registration-response";

@Injectable({
    providedIn: "root"
})
export class AccountService extends BaseDataService {
    static readonly TwoFactorEnabledEventName = "TwoFactorEnabled";
    static readonly UserLoginEventName = "UserLogin";
    static readonly UserLogoutEventName = "UserLogout";

    private loginPromise: Promise<AuthenticationSettings>;
    private logoutPromise: Promise<void>;

    url = "api/account";

    constructor(
        protected http: HttpClient,
        protected handlersFactory: HttpResponsesHandlersFactory,
        private broadcastService: BroadcastService,
        private messageTrackingService: MessageTrackingService
    ) {
        super(http, handlersFactory);
    }

    login(request: LoginRequest): Promise<AuthenticationSettings> {
        if (!this.loginPromise) {
            this.loginPromise = this.handleRequest<AuthenticationSettings>(
                this.http.post(`${this.url}/login`, request),
                this.handlersFactory.getDefault({ showErrorMessage: false })
            )
                .then(authenticationSettings => {
                    this.loginPromise = null;

                    if (authenticationSettings.loggedUser) {
                        this.broadcastService.broadcast(AccountService.UserLoginEventName, authenticationSettings.loggedUser);
                    }

                    return authenticationSettings;
                })
                .catch(errorResponse => {
                    this.loginPromise = null;
                    throw errorResponse;
                });
        }
        return this.loginPromise;
    }

    linkNHSLogin(request: LinkNHSRequest): Promise<string> {
        return this.handleRequest<any>(this.http.post<any>(`${this.url}/link-nhs`, request), this.handlersFactory.getDefault());
    }

    unlinkNHSLogin(): Promise<any> {
        return this.handleRequest<any>(this.http.post<any>(`${this.url}/unlink-nhs`, {}), this.handlersFactory.getDefault());
    }

    async saveNHSRegistrationInfo(authCode: string, redirectUrl: string, organizationUrl: string, externalId: string) {
        const nhsNonce = AppStorage.getItemFromSession<string>(AppStorage.NhsNonceKey);
        const body = { authCode, redirectUrl, organizationUrl, externalId, nhsNonce };

        const saveResponse = await this.handleRequest(
            this.http.post<NhsRegistrationResponse>(`${this.url}/nhs-registration`, body),
            this.handlersFactory.getDefault({ showErrorMessage: false })
        );

        if (saveResponse?.completeRegistrationData) {
            saveResponse.completeRegistrationData.dateOfBirth = SetLocalTimeZone(saveResponse.completeRegistrationData.dateOfBirth);
        }

        if (saveResponse?.patientAction === SaveNHSTokenRegistrationPatientAction.LoggedIn) {
            AppStorage.setItem(AppStorage.LoginInProcess, true);
        }

        return saveResponse;
    }

    logout(): Promise<void> {
        if (!this.logoutPromise) {
            this.logoutPromise = this.handleRequest(this.http.post(`${this.url}/logout`, null), this.handlersFactory.getDefault())
                .then(() => {
                    this.logoutPromise = null;
                    this.broadcastService.broadcast(AccountService.UserLogoutEventName);
                    AppStorage.setItem(AppStorage.ImpersonationDataKey, null);
                    this.messageTrackingService.hasShownLimitAccessMessage = false;

                    AppStorage.setItem(
                        AppStorage.IsRegisteredErrorMessage,
                        JSON.stringify({
                            errorMessage: null,
                            patientId: null
                        })
                    );
                })
                .catch((response: HttpErrorResponse) => {
                    if (response.status == 401) {
                        this.broadcastService.broadcast(AccountService.UserLogoutEventName);
                        AppStorage.setItem(AppStorage.ImpersonationDataKey, null);
                    }

                    this.logoutPromise = null;
                });
        }

        AppStorage.setItem(AppStorage.LoggedOfflineKey, null); // Offline support
        AppStorage.setItem(AppStorage.NhsLoginKey, null);
        AppStorage.setItem("attemptedUrl", "");
        AppStorage.setItem("selectedPatient", null);
        AppStorage.setItem("currentPatientId", null);

        return this.logoutPromise;
    }

    registerAccount(userObj: any): Promise<ISignUpResult> {
        return this.handleRequest<ISignUpResult>(
            this.http.post<ISignUpResult>(`${this.url}/register`, userObj),
            this.handlersFactory.getDefault()
        );
    }

    resetPassword(data: any): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/reset-password`, data));
    }

    resetOfflinePin(data: any): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/reset-offline-pin`, data));
    }

    async activateUser(data: any) {
        const activateResult = await this.handleRequest(
            this.http.post<any>(`${this.url}/activate`, data),
            this.handlersFactory.getDefault({ showErrorMessage: false })
        );

        if (activateResult.dateOfBirthMismatch) {
            activateResult.dateOfBirthMismatch = new DateOfBirthMismatchNormalized(activateResult.dateOfBirthMismatch);
        }

        return <ActivateAccountResult>activateResult;
    }

    confirmEmail(data: any): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/confirm-email`, data));
    }

    recoverPassword(data: RecoverPassword): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/recover-password`, data));
    }

    recoverOfflinePin(data: RecoverPassword): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/recover-offline-pin`, data));
    }

    getReCaptchaSiteKey(): Promise<string> {
        return this.handleRequest(
            this.http.get<string>(`${this.url}/recaptcha-site-key`),
            this.handlersFactory.getDefault({ showErrorMessage: false })
        );
    }

    checkIfIpIsBlocked(): Promise<any> {
        return this.handleRequest(this.http.get(`${this.url}/ip-locking-time`), this.handlersFactory.getDefault());
    }
    enableAuthenticator(): Promise<any> {
        return this.handleRequest(this.http.get(`${this.url}/me/2fa-enabling-data`), this.handlersFactory.getDefault());
    }

    verificationCode(data: any): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/me/enable-2fa`, data), this.handlersFactory.getDefault());
    }

    enableU2F(): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/me/enable-u2f`, null), this.handlersFactory.getDefault());
    }

    disableU2F(): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/me/disable-u2f`, null), this.handlersFactory.getDefault());
    }

    disable2fa(): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/me/disable-2fa`, null), this.handlersFactory.getDefault());
    }

    disable2faForUser(userId: string): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/disable-2fa/${userId}`, null), this.handlersFactory.getDefault());
    }

    get2faU2fRecoveryCodes(): Promise<string[]> {
        return this.handleRequest(this.http.get<string[]>(`${this.url}/me/2fa-u2f-recovery-codes`), this.handlersFactory.getDefault());
    }

    generateServerChallenge(): Promise<U2fRegistrationRequest> {
        return this.handleRequest(
            this.http.get<U2fRegistrationRequest>(`${this.url}/me/generate-u2f-device-registration-challenge`),
            this.handlersFactory.getDefault()
        );
    }

    registerU2fDevice(deviceResponse: U2fRegistrationResponse): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/me/register-u2f-device`, deviceResponse), this.handlersFactory.getDefault());
    }

    authenticateU2fDevice(deviceResponse: U2fAuthenticationResponse): Promise<any> {
        return this.handleRequest(this.http.post(`${this.url}/authenticate-u2f-device`, deviceResponse), this.handlersFactory.getDefault());
    }

    loginWithRecoveryCode(data: RecoveryCode) {
        return this.handleRequest(
            this.http.post<UserLogin | PatientLogin>(`${this.url}/recovery-code-login`, data),
            this.handlersFactory.getDefault()
        ).then(loggedUser => {
            this.broadcastService.broadcast(AccountService.UserLoginEventName, loggedUser);
            return loggedUser;
        });
    }

    checkRecoveryCodeAvailability(userId: string, recoveryCode: string): Promise<any> {
        return this.handleRequest(
            this.http.get(`${this.url}/check-recovery-code`, { params: new HttpParams({ fromObject: { userId, code: recoveryCode } }) }),
            this.handlersFactory.getDefault()
        );
    }

    checkPinRecoveryCodeAvailability(userId: string, recoveryCode: string): Promise<any> {
        return this.handleRequest(
            this.http.get(`${this.url}/check-offline-pin-recovery-code`, {
                params: new HttpParams({ fromObject: { userId, code: recoveryCode } })
            }),
            this.handlersFactory.getDefault()
        );
    }

    getToken(): Promise<any> {
        return this.handleRequest<any>(this.http.get<any>(`${this.url}/token`), this.handlersFactory.getDefault());
    }

    getAccountActivationInfo(loginId: string, code: string): Promise<any> {
        return this.handleRequest(
            this.http.get(`${this.url}/activation-info`, { params: new HttpParams({ fromObject: { loginId, code } }) }),
            this.handlersFactory.getDefault()
        );
    }

    getLinkedOrgsInfo() {
        return this.handleRequest(
            this.http.get<{ userLogins: UserLogins[] }>(`${this.url}/linked-orgs`),
            this.handlersFactory.getDefault({
                showSuccessMessage: false,
                errorMessage: "Error loading the linked orgs information"
            })
        );
    }

    getIm1APISettings(): Promise<IM1ApiSettings> {
        return this.handleRequest<IM1ApiSettings>(
            this.http.get<IM1ApiSettings>(`${this.url}/get-im1-settings`),
            this.handlersFactory.getDefault()
        );
    }

    isAuthenticatorEnabled(email: string): Promise<boolean> {
        return this.handleRequest(
            this.http.get<boolean>(`${this.url}/is-authenticator-enabled`, {
                params: { email }
            }),
            this.handlersFactory.getDefault()
        );
    }
}
